import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { mktReport, apiError } from "../../../lib/adminApi";
import { Panel, Kpi, Empty, fmtMoney } from "../ui";
import { ChannelBadge } from "./LinksPanel";

const PERIODS = [["7", "7 dias"], ["30", "30 dias"], ["90", "90 dias"], ["0", "Tudo"]];
const pct = (v) => (v == null ? "—" : `${String(v).replace(".", ",")}%`);

export default function ReportPanel() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);

  useEffect(() => { mktReport(Number(days)).then(setData).catch((e) => toast.error(apiError(e))); }, [days]);

  const t = data?.totals;
  const rows = (data?.rows || []).filter((r) => r.link_key === "organic" || !r.archived || r.visits || r.signups || r.paid);

  return (
    <div className="space-y-6" data-testid="mkt-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 rounded-xl bg-white/[0.03] ring-1 ring-white/[0.06] p-1">
          {PERIODS.map(([v, l]) => (
            <button key={v} onClick={() => setDays(v)} data-testid={`mkt-period-${v}`} className={`h-8 px-3 rounded-lg text-[12.5px] ${days === v ? "bg-[#38BDF8]/25 text-white ring-1 ring-[#38BDF8]/40" : "text-slate-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
        {data?.ga && (
          <div className="text-[11.5px] text-slate-500" data-testid="mkt-ga-status">
            GA4 <code className="text-slate-300">{data.ga.measurement_id}</code> · compras server-side (Measurement Protocol): {data.ga.mp_configured ? <span className="text-emerald-400">ativas</span> : <span className="text-amber-300">aguardando GA_API_SECRET</span>}
          </div>
        )}
      </div>

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <Kpi label="Visitas" value={t.visits} hint={`${t.visitors} visitantes únicos`} testid="mkt-kpi-visits" />
          <Kpi label="Cadastros" value={t.signups} testid="mkt-kpi-signups" />
          <Kpi label="1º orçamento" value={t.first_quotes} testid="mkt-kpi-quotes" />
          <Kpi label="Testes iniciados" value={t.trials} testid="mkt-kpi-trials" />
          <Kpi label="Assinaturas pagas" value={t.paid} hint={`${t.active_subs} ativas hoje`} testid="mkt-kpi-paid" />
          <Kpi label="MRR atribuído" value={fmtMoney(t.mrr)} accent testid="mkt-kpi-mrr" />
        </div>
      )}

      <Panel title="Funil por link / campanha" testid="mkt-report-table">
        {!rows.length ? <Empty text="Sem dados no período." /> : (
          <div className="rounded-2xl ring-1 ring-white/[0.06] overflow-hidden"><div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-white/[0.03] text-[10.5px] uppercase tracking-wider text-slate-400">
                <tr>{["Link", "Canal", "Visitas", "Cadastros", "Conv.", "1º orç.", "Testes", "Pagas", "Conv. paga", "MRR"].map((h, i) => <th key={h} className={`px-3 py-2.5 font-medium ${i < 2 ? "text-left" : "text-right"}`}>{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-white/[0.05]">
                {rows.map((r) => (
                  <tr key={r.link_key} data-testid={`mkt-row-${r.link_key}`} className="hover:bg-white/[0.02]">
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-white">{r.name}</div>
                      {r.utm_campaign && <div className="text-[11px] text-slate-500">{r.utm_source} · {r.utm_campaign} · <code>{r.link_key}</code></div>}
                    </td>
                    <td className="px-3 py-2.5"><ChannelBadge channel={r.channel} label={r.channel_label} /></td>
                    <td className="px-3 py-2.5 text-right font-mono-jb">{r.visits}<span className="text-slate-500"> / {r.visitors}</span></td>
                    <td className="px-3 py-2.5 text-right font-mono-jb">{r.signups}</td>
                    <td className="px-3 py-2.5 text-right text-slate-400">{pct(r.conv_signup)}</td>
                    <td className="px-3 py-2.5 text-right font-mono-jb">{r.first_quotes}</td>
                    <td className="px-3 py-2.5 text-right font-mono-jb">{r.trials}</td>
                    <td className="px-3 py-2.5 text-right font-mono-jb text-emerald-300">{r.paid}</td>
                    <td className="px-3 py-2.5 text-right text-slate-400">{pct(r.conv_paid)}</td>
                    <td className="px-3 py-2.5 text-right font-mono-jb text-[#7DD3FC]">{fmtMoney(r.mrr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div></div>
        )}
        <p className="mt-3 text-[11.5px] text-slate-500">Visitas = visitante único por dia e por link (recarregar a página não conta de novo). MRR = assinaturas ativas hoje atribuídas ao link (first-touch, 90 dias). "Pagas" = usuários cujo primeiro pagamento confirmado pelo Stripe caiu no período.</p>
      </Panel>

      {!!data?.sources?.length && (
        <Panel title="Cadastros sem link — por origem" testid="mkt-sources">
          <div className="flex flex-wrap gap-2">
            {data.sources.map((s) => (
              <span key={s.source} className="inline-flex items-center gap-2 rounded-full bg-white/[0.03] ring-1 ring-white/[0.08] px-3 h-8 text-[12px]">
                <span className="text-slate-200">{s.source}</span><span className="font-mono-jb text-slate-400">{s.signups}</span>{s.paid > 0 && <span className="text-emerald-300">{s.paid} pagas</span>}
              </span>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}
